import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import { markRaw } from 'vue';

import { InFlightCell } from '@/common/helpers';
import type { ZoneAlertResponse } from '../alerts/zone-batch.client';
import { DeforestationAlertsClient } from './deforestation.client';
import type { AlertsRaddZone, GladRaddLayer } from '@/common/modules';
import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import { deforestationAlertCacheKey, useDeforestationStore } from './deforestation.store';
import { DEFAULT_DEFORESTATION_LAYER } from './deforestation.types';
import type {
  DeforestationLayerChoice,
  DeforestationSubscriptionStatus,
} from './deforestation.types';

/** Response of `GET`/`PUT /deforestation/zone/{id}/layer`. */
interface DeforestationLayerResponse {
  zone_id: number;
  layer: string;
}

// Wire values are `radd` / `glad` / `glad_radd`; internally we use the layer
// *names* `radd` / `glad` / `glad-radd` (hyphen) so the choice maps 1:1 to the
// built layer. Only the combined layer's wire form differs (underscore).
const toWireLayer = (choice: DeforestationLayerChoice): 'radd' | 'glad' | 'glad_radd' => {
  if (choice === 'glad-radd') {
    return 'glad_radd';
  }
  if (choice === 'glad') {
    return 'glad';
  }
  return 'radd';
};
const fromWireLayer = (wire: string): DeforestationLayerChoice => {
  if (wire === 'glad_radd') {
    return 'glad-radd';
  }
  if (wire === 'glad') {
    return 'glad';
  }
  return 'radd';
};

@Composable()
export class useDeforestationService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useDeforestationStore())
  public readonly deforestationStore!: ReturnType<typeof useDeforestationStore>;

  /**
   * One batch client per product. It owns the in-flight dedup that used to live
   * here, which is why BOTH read paths below go through it: a singular read and
   * a batch read for the same zone must join the same request, exactly as they
   * did when one map served both. Same wiring as `useFireAlertsService`.
   */
  private readonly clients = new Map<DeforestationLayerChoice, DeforestationAlertsClient>();

  private client(layer: DeforestationLayerChoice): DeforestationAlertsClient {
    let client = this.clients.get(layer);
    if (!client) {
      client = new DeforestationAlertsClient(
        (path) => this.api.get<AlertsRaddZone>(`${import.meta.env.VITE_ALERTS_API}${path}`),
        layer,
        (id) => this.deforestationStore.alerts[deforestationAlertCacheKey(id, layer)],
        (id, response) =>
          this.deforestationStore.addAlert(deforestationAlertCacheKey(id, layer), response),
      );
      this.clients.set(layer, markRaw(client));
    }
    return client;
  }
  private readonly layerInFlight = new InFlightCell<ApiResponse<{ tileUrl: string }>>();
  private readonly gladLayerInFlight = new InFlightCell<ApiResponse<{ tileUrl: string }>>();
  private readonly gladRaddLayerInFlight = new InFlightCell<ApiResponse<GladRaddLayer>>();

  public async getRadd(): Promise<ApiResponse<{ tileUrl: string }>> {
    if (this.deforestationStore.layer) {
      return this.deforestationStore.layer;
    }
    return this.layerInFlight.runOnce(async () => {
      const res = await this.api.get<{ tileUrl: string }>(
        `${import.meta.env.VITE_GEE_API}/radd/layer`,
      );
      if (res.isSuccess && res.data) {
        this.deforestationStore.setLayer(res);
      }
      return res;
    });
  }

  /**
   * GLAD-L only (Landsat optical) deforestation tile layer. Same `{ tileUrl }`
   * shape/auth as `getRadd`, distinct endpoint + cache cell so the layers stay
   * independent. See `/glad/layer`.
   */
  public async getGlad(): Promise<ApiResponse<{ tileUrl: string }>> {
    if (this.deforestationStore.gladLayer) {
      return this.deforestationStore.gladLayer;
    }
    return this.gladLayerInFlight.runOnce(async () => {
      const res = await this.api.get<{ tileUrl: string }>(
        `${import.meta.env.VITE_GEE_API}/glad/layer`,
      );
      if (res.isSuccess && res.data) {
        this.deforestationStore.setGladLayer(res);
      }
      return res;
    });
  }

  /**
   * RADD + GLAD-L integrated ("combined") deforestation alert tile layer. Same
   * shape/auth as `getRadd`, distinct endpoint and cache cell so the two
   * layers stay independent. See `/glad-radd/layer`.
   */
  public async getGladRadd(): Promise<ApiResponse<GladRaddLayer>> {
    if (this.deforestationStore.gladRaddLayer) {
      return this.deforestationStore.gladRaddLayer;
    }
    return this.gladRaddLayerInFlight.runOnce(async () => {
      const res = await this.api.get<GladRaddLayer>(
        `${import.meta.env.VITE_GEE_API}/glad-radd/layer`,
      );
      if (res.isSuccess && res.data) {
        this.deforestationStore.setGladRaddLayer(res);
      }
      return res;
    });
  }

  /**
   * Deforestation alerts for MANY zones, in one request per product.
   *
   * `GET /{layer}/zones?ids=…` replaced the per-zone fan-out this used to do
   * (contract `alerts-batch-by-zone-ids`). The client still bounds a fan-out,
   * but only for the zones the batch could not answer: the batch is cache-only,
   * so a zone the catalogue has never computed falls back to the single-zone
   * route, which computes it. On the non-default layers that is most zones
   * (Delta 3), so the batch is a clear win on `radd` and roughly break-even on
   * `glad` / `glad-radd` until the catalogue precomputes them.
   *
   * Best-effort per zone: a user-drawn child with no precomputed alerts must not
   * cost the rest, so a per-zone failure comes back as a failed `ApiResponse`
   * rather than rejecting.
   */
  public async getAlertsByZoneIds(
    zoneIds: number[],
    layer: DeforestationLayerChoice,
  ): Promise<ZoneAlertResponse<AlertsRaddZone>[]> {
    return this.client(layer).getByZoneIds(zoneIds);
  }

  // Email-subscription endpoints — byte-for-byte the same request/response
  // shapes as `useFireAlertsService`, only the `/deforestation` path segment
  // differs. The two subscriptions are fully independent.
  public async getSubscriptionStatus(zoneId?: number): Promise<ApiResponse<DeforestationSubscriptionStatus>> {
    const url = zoneId != null
      ? `${import.meta.env.VITE_ALERTS_API}/deforestation/notifications/subscribe?zone_id=${String(zoneId)}`
      : `${import.meta.env.VITE_ALERTS_API}/deforestation/notifications/subscribe`;
    return this.api.get<DeforestationSubscriptionStatus>(url);
  }

  // The backend accepts a `zones: number[]` body and subscribes them atomically in
  // a single DynamoDB write. Passing a single ID still works — it just goes into a
  // one-element array. Use the array form whenever you have multiple zones to
  // subscribe at once; one HTTP call is much cheaper than fanning out, and it
  // sidesteps the read-modify-write race that parallel single-zone calls trigger.
  public async subscribe(zoneIds: number | number[]): Promise<ApiResponse<DeforestationSubscriptionStatus>> {
    const zones = Array.isArray(zoneIds) ? zoneIds : [zoneIds];
    return this.api.put<DeforestationSubscriptionStatus>(
      `${import.meta.env.VITE_ALERTS_API}/deforestation/notifications/subscribe`,
      { zones },
    );
  }

  public async unsubscribe(zoneId?: number): Promise<ApiResponse<DeforestationSubscriptionStatus>> {
    const url = zoneId != null
      ? `${import.meta.env.VITE_ALERTS_API}/deforestation/notifications/subscribe?zone_id=${String(zoneId)}`
      : `${import.meta.env.VITE_ALERTS_API}/deforestation/notifications/subscribe`;
    return this.api.delete<DeforestationSubscriptionStatus>(url);
  }

  // ── Per-parent-zone deforestation layer preference (RADD / GLAD-L / RADD+GLAD) ──
  // Drives both the deforestation map layer and the email alerts (the latter is
  // backend-only). `zone_id` is the parent/`zone:01` id. An invalid layer value
  // is rejected by the backend (422); we treat any non-2xx as a no-op here.

  /**
   * GET the parent zone's configured deforestation layer. Cache-first — the
   * store is the cache. A never-set zone returns `radd` from the backend, so no
   * special-casing. Populates the store and returns the resolved choice.
   */
  public async getLayerPreference(parentZoneId: number): Promise<DeforestationLayerChoice> {
    const cached = this.deforestationStore.layerPreferences[parentZoneId];
    if (cached !== undefined) {
      return cached;
    }
    const res = await this.api.get<DeforestationLayerResponse>(
      `${import.meta.env.VITE_ALERTS_API}/deforestation/zone/${parentZoneId}/layer`,
    );
    const choice = res.isSuccess && res.data
      ? fromWireLayer(res.data.layer)
      : DEFAULT_DEFORESTATION_LAYER;
    this.deforestationStore.setLayerPreference(parentZoneId, choice);
    return choice;
  }

  /**
   * PUT the new choice and reflect the layer the backend echoes back, so the map
   * + alert menu re-render against the chosen layer. Returns the applied choice.
   */
  public async setLayerPreference(
    parentZoneId: number,
    layer: DeforestationLayerChoice,
  ): Promise<DeforestationLayerChoice> {
    const res = await this.api.put<DeforestationLayerResponse>(
      `${import.meta.env.VITE_ALERTS_API}/deforestation/zone/${parentZoneId}/layer`,
      { layer: toWireLayer(layer) },
    );
    const applied = res.isSuccess && res.data ? fromWireLayer(res.data.layer) : layer;
    this.deforestationStore.setLayerPreference(parentZoneId, applied);
    return applied;
  }
}
